import Stack from "@mui/material/Stack";
import CookiesModalContext from "components/CookiesModalContext";
import LanguageContext from "components/LanguageContext";
import SiteContext from "components/SiteContext";
import Link from "components/templatesComponents/Link";
import { useGetFooter } from "hooks/section.hook";
import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";
import { eventKey } from "utils/commonUtils";
import t from "utils/locales/translation.json";
import generateTemplatePropsFromContents from "utils/templatePropsUtils";

const Footer = (props) => {
  const { homePageLink = "", homePageAntsLink = "", isMourning = false } = props;
  const { handleOpenCookiesModal } = useContext(CookiesModalContext);
  const { language } = useContext(LanguageContext);

  const site = useContext(SiteContext);
  const { data: footer } = useGetFooter(site.id);

  const footerContents = (footer && generateTemplatePropsFromContents(footer.contents)) || {};
  let {
    LinkExternal: footerLinkFR = [],
    LinkExternal1: footerLinkFR1 = [],
    LinkExternal2: footerLinkFR2 = [],
    LinkInternal1: LinkInternalFR1 = [],
    LinkInternal2: LinkInternalFR2 = [],
    LinkExternalEN: footerLinkEN = [],
    LinkExternalEN1: footerLinkEN1 = [],
    LinkExternalEN2: footerLinkEN2 = [],
    LinkInternalEN1 = [],
    LinkInternalEN2 = [],
  } = footerContents;
  if (!Array.isArray(footerLinkFR)) {
    footerLinkFR = [footerLinkFR];
  }
  if (!Array.isArray(footerLinkFR1)) {
    footerLinkFR1 = [footerLinkFR1];
  }
  if (!Array.isArray(footerLinkFR2)) {
    footerLinkFR2 = [footerLinkFR2];
  }
  if (!Array.isArray(LinkInternalFR1)) {
    LinkInternalFR1 = [LinkInternalFR1];
  }
  if (!Array.isArray(LinkInternalFR2)) {
    LinkInternalFR2 = [LinkInternalFR2];
  }
  if (!Array.isArray(footerLinkEN)) {
    footerLinkEN = [footerLinkEN];
  }
  if (!Array.isArray(footerLinkEN1)) {
    footerLinkEN1 = [footerLinkEN1];
  }
  if (!Array.isArray(footerLinkEN2)) {
    footerLinkEN2 = [footerLinkEN2];
  }
  if (!Array.isArray(LinkInternalEN1)) {
    LinkInternalEN1 = [LinkInternalEN1];
  }
  if (!Array.isArray(LinkInternalEN2)) {
    LinkInternalEN2 = [LinkInternalEN2];
  }

  const LinkInternal1Displayed = React.useMemo(() => {
    if (language.toUpperCase() === "EN") {
      return LinkInternalEN1;
    }
    return LinkInternalFR1 || [];
  }, [language, LinkInternalEN1, LinkInternalFR1]);

  const LinkInternal2Displayed = React.useMemo(() => {
    if (language.toUpperCase() === "EN") {
      return LinkInternalEN2;
    }
    return LinkInternalFR2 || [];
  }, [language, LinkInternalEN2, LinkInternalFR2]);

  const { name: siteName, settings: siteSettings } = site || {};
  let { socialNetworks = [] } = siteSettings || {};

  if (socialNetworks && !Array.isArray(socialNetworks)) {
    socialNetworks = [socialNetworks];
  }

  const LinkListArray = useMemo(() => {
    if (language === "EN") {
      return [footerLinkEN, footerLinkEN1, footerLinkEN2];
    }
    return [footerLinkFR, footerLinkFR1, footerLinkFR2];
  }, [language, footerLinkFR, footerLinkFR1, footerLinkFR2, footerLinkEN, footerLinkEN1, footerLinkEN2]);

  const LinkList1 = () =>
    (LinkInternal1Displayed &&
      LinkInternal1Displayed.map((list1item) => (
        <li className="fr-footer__bottom-item" key={list1item.id}>
          <Link basic className="fr-footer__bottom-link" {...list1item.link}>
            {list1item.title}
          </Link>
        </li>
      ))) ||
    null;

  const LinkList2 = () =>
    (LinkInternal2Displayed &&
      LinkInternal2Displayed.map((list2item) => (
        <li className="fr-footer__bottom-item" key={list2item.id}>
          <Link className="fr-footer__bottom-link" {...list2item.link}>
            {list2item.title}
          </Link>
        </li>
      ))) ||
    null;

  const handleKeyDown = (event) => {
    if (event.key === eventKey) {
      handleOpenCookiesModal(event);
    }
  };

  const hasFooterLinks = useMemo(() => LinkListArray.some((l) => l.length > 0), [LinkListArray]);

  return (
    <div className="fr-footer" role="contentinfo">
      {hasFooterLinks && (
        <div className="fr-footer__top">
          <div className="fr-container">
            <div className="fr-grid-row fr-grid-row--start fr-grid-row--gutters">
              {LinkListArray.map((linkList) => {
                const [firstLink, ...links] = linkList;

                return (
                  <div className="fr-col-12 fr-col-sm-6 fr-col-md-4" key={`linkList-${firstLink?.id}`}>
                    {firstLink ? <h3 className="fr-footer__top-cat">{firstLink.title}</h3> : null}
                    <ul className="fr-footer__top-list">
                      {links.map((list1item) => (
                        <li key={list1item.id}>
                          <Link basic {...list1item.link} className="fr-footer__top-link">
                            {list1item.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="fr-container">
        <div className="fr-footer__body">
          {((isMourning && typeof document !== "undefined" && typeof document.documentElement !== "undefined") ||
            !isMourning) && (
            <div className="fr-footer__brand fr-enlarge-link" src="/logo-republique-francaise.svg">
              <Link url={homePageLink} title={t[language].components.france_titres_logo_title} sx={{ mr: 3 }}>
                <p className="fr-logo">
                  république
                  <br />
                  française
                </p>
              </Link>
              <img
                src={isMourning ? "/logo_France_Titres_deuil.svg" : "/logo_France_Titres_clair.svg"}
                alt=""
                title={t[language].components.france_titres_logo_title}
              />
            </div>
          )}
          <div className="fr-footer__content">
            <p className="fr-footer__content-desc">
              {t[language].footer.description}
              <Link
                className="fr-footer__content-desc"
                url={homePageAntsLink}
                external
                basic
                title={`Agence nationale des titres sécurisés - ${t[language].components.new_tab}`}
              >
                Agence nationale des titres sécurisés
              </Link>
            </p>
            <ul className="fr-footer__content-list">
              <li className="fr-footer__content-item">
                <Link
                  external
                  className="fr-footer__content-link"
                  url="https://info.gouv.fr"
                  title={`info.gouv.fr - ${t[language].components.new_tab}`}
                >
                  info.gouv.fr
                </Link>
              </li>
              <li className="fr-footer__content-item">
                <Link
                  external
                  className="fr-footer__content-link"
                  url="https://service-public.gouv.fr"
                  title={`service-public.gouv.fr - ${t[language].components.new_tab}`}
                >
                  service-public.gouv.fr
                </Link>
              </li>
              <li className="fr-footer__content-item">
                <Link
                  external
                  className="fr-footer__content-link"
                  url="https://legifrance.gouv.fr"
                  title={`legifrance.gouv.fr - ${t[language].components.new_tab}`}
                >
                  legifrance.gouv.fr
                </Link>
              </li>
              <li className="fr-footer__content-item">
                <Link
                  external
                  className="fr-footer__content-link"
                  url="https://data.gouv.fr"
                  title={`data.gouv.fr - ${t[language].components.new_tab}`}
                >
                  data.gouv.fr
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="fr-footer__bottom">
          <Stack
            sx={{ alignItems: { xs: "center", sm: "flex-start" }, fontSize: "14px", py: 2, mb: -5, width: "100%" }}
          >
            <h3 className="fr-text--sm fr-text--regular fr-m-0">{t[language].footer.partners}</h3>
            <Stack
              direction={{ sm: "row" }}
              spacing={{ xs: 1, sm: 2 }}
              sx={{ alignItems: "center", mt: 1, "& a": { backgroundImage: "none" } }}
            >
              {["ANTS", "IMMAT"].includes(siteName) && (
                <Link url="https://europa.eu/youreurope/index.htm#en" title="Your Europe">
                  <img src="/logo-yourEurope.svg" alt="Logo your Europe" />
                </Link>
              )}
              <Link
                url="https://www.plus.transformation.gouv.fr/"
                title="Services Publics +"
                sx={{ p: 3, "& > img": { width: 160 } }}
              >
                <img src="/logo-servicesPublicsPlus.webp" alt="Logo Services Publics +" />
              </Link>
            </Stack>
          </Stack>
        </div>
        <div className="fr-footer__bottom">
          <ul className="fr-footer__bottom-list">
            <LinkList1 />
            <li className="fr-footer__bottom-item">
              <Link
                basic
                className="fr-footer__bottom-link"
                onClick={handleOpenCookiesModal}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                sx={{ fontSize: "0.75rem", "a&:not([href])": { cursor: "pointer" } }}
              >
                {t[language].footer.cookies_link}
              </Link>
            </li>
            <LinkList2 />
          </ul>
          <div className="fr-footer__bottom-copy">
            <p>
              {t[language].footer.etalab}{" "}
              <Link
                basic
                external
                url="https://www.etalab.gouv.fr/licence-ouverte-open-licence"
                title={`Licence etalab-2.0 - ${t[language].components.new_tab}`}
              >
                licence etalab-2.0
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

Footer.propTypes = {
  homePageLink: PropTypes.string,
  homePageAntsLink: PropTypes.string,
  isMourning: PropTypes.bool,
};

export default Footer;
